import { CSSProperties, useCallback, useMemo, useState } from 'react'
import { MapContainer, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import { motion } from 'motion/react'
import 'leaflet.markercluster'
import { getCoordinatesFromMotifs } from '../../utils/coordinates'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useEnv } from '../../utils/useEnv'
import {
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_SOURCE,
  MAP_DEFAULT_ZOOM,
  MAP_LOCAL_TILE_URL,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MAP_ONLINE_TILE_URL,
  MAP_SOURCE_STORAGE_KEY,
  MapSource,
  MOTIF_MARKER_SIZE,
} from './mapConfig'
import MapController from './MapController'
import MotifClusterLayer from './MotifClusterLayer'
import PinCascade from './PinCascade'
import { mapVariants } from './filterAnimation'
import { useMapReveal } from './useMapReveal'
import './map.css'
import { cn } from '../../utils/cn'

const mapSourceButtonClassName = 'border-0 px-[0.6rem] py-[0.4rem] text-[0.75rem]'

// used by 'map.css'
const markerSizeVariable = { '--motif-marker-size': `${MOTIF_MARKER_SIZE}px` } as CSSProperties

interface AttributionSource {
  label: string
  url: string
}

const STAMEN: AttributionSource = { label: 'Stamen Design', url: 'https://stamen.com/' }
const OPEN_MAP_TILES: AttributionSource = {
  label: 'OpenMapTiles',
  url: 'https://openmaptiles.org/',
}
const OPEN_STREET_MAP: AttributionSource = {
  label: 'OpenStreetMap contributors',
  url: 'https://openstreetmap.org/copyright',
}

// self-hosted tiles: OpenStreetMap data (ODbL) in the OpenMapTiles schema, rendered with the Stamen Toner style
const ATTRIBUTIONS: Record<MapSource, AttributionSource[]> = {
  online: [
    { label: 'Stadia Maps', url: 'https://stadiamaps.com/' },
    STAMEN,
    OPEN_MAP_TILES,
    OPEN_STREET_MAP,
  ],
  local: [STAMEN, OPEN_MAP_TILES, OPEN_STREET_MAP],
}

// the whole world must stay reachable (pins can be anywhere), just stop panning at its edges instead of looping
// latitude is clamped to Mercator's valid range (~85.05); using +/-90 breaks Leaflet's bounds math
const MAX_BOUNDS = L.latLngBounds([-85, -180], [85, 180])

const MapView = () => {
  const { isLocalDev, isKiosk } = useEnv()
  // the build's env decides the source; only local dev may override it with the switch below
  const [mapSource, setMapSource] = useState<MapSource>(() => {
    const storedSource = isLocalDev ? localStorage.getItem(MAP_SOURCE_STORAGE_KEY) : null
    return storedSource === 'online' || storedSource === 'local' ? storedSource : MAP_DEFAULT_SOURCE
  })
  const { allMotifs, setTargetedMotif, geographicMapViewport } = useContextMotifDetails()
  const { isRevealed, handleTilesLoad } = useMapReveal()

  const changeMapSource = useCallback((source: MapSource) => {
    setMapSource(source)
    localStorage.setItem(MAP_SOURCE_STORAGE_KEY, source)
  }, [])

  // kiosk visitors must not be able to leave the app, so the credits are plain text there
  const attribution = useMemo(
    () =>
      ATTRIBUTIONS[mapSource]
        .map(({ label, url }) =>
          isKiosk ? `&copy; ${label}` : `&copy; <a href="${url}" target="_blank">${label}</a>`,
        )
        .join(', '),
    [mapSource, isKiosk],
  )

  const coordinates = useMemo(() => getCoordinatesFromMotifs(allMotifs), [allMotifs])

  const markerData = useMemo(() => {
    const motifsById = new Map(allMotifs.map(motif => [motif.id, motif]))
    return coordinates.map(coordinate => ({
      coordinate,
      motif: motifsById.get(coordinate.id),
    }))
  }, [allMotifs, coordinates])

  const handleMotifSelect = useCallback(
    (motifId: number) => setTargetedMotif(motifId),
    [setTargetedMotif],
  )

  if (coordinates.length === 0) {
    return (
      <div className="geo-map relative z-0 flex size-full items-center justify-center">
        <div>No coordinates available</div>
      </div>
    )
  }

  // it is hidden until its tiles are in, then it fades in and its pins pop in
  return (
    <motion.div
      className="geo-map relative z-0 flex size-full items-center justify-center"
      style={markerSizeVariable}
      variants={mapVariants}
      initial="hidden"
      animate={isRevealed ? 'visible' : 'hidden'}
      exit="hidden"
    >
      {isLocalDev && (
        <div
          className="absolute right-[1rem] top-[1rem] z-[1000] flex gap-[0.25rem] rounded-[0.25rem] bg-white p-[0.25rem] shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
          role="group"
          aria-label="Map source"
        >
          <button
            type="button"
            className={cn(mapSourceButtonClassName, {
              'bg-red text-white': mapSource === 'online',
              'bg-transparent text-black': mapSource !== 'online',
            })}
            onClick={() => changeMapSource('online')}
          >
            Online map
          </button>
          <button
            type="button"
            className={cn(mapSourceButtonClassName, {
              'bg-red text-white': mapSource === 'local',
              'bg-transparent text-black': mapSource !== 'local',
            })}
            onClick={() => changeMapSource('local')}
          >
            Local map
          </button>
        </div>
      )}
      <MapContainer
        className="size-full"
        center={geographicMapViewport.current?.center || MAP_DEFAULT_CENTER}
        zoom={geographicMapViewport.current?.zoom || MAP_DEFAULT_ZOOM}
        minZoom={MAP_MIN_ZOOM}
        maxZoom={MAP_MAX_ZOOM}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        scrollWheelZoom={true}
        maxBounds={MAX_BOUNDS}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          key={mapSource}
          noWrap
          attribution={attribution}
          url={mapSource === 'online' ? MAP_ONLINE_TILE_URL : MAP_LOCAL_TILE_URL}
          eventHandlers={{
            load: handleTilesLoad,
            tileerror: () => {
              if (mapSource === 'online') changeMapSource('local')
            },
          }}
        />
        <MapController coordinates={coordinates} />
        <MotifClusterLayer markers={markerData} onSelect={handleMotifSelect} />
        <PinCascade isRevealed={isRevealed} />
      </MapContainer>
    </motion.div>
  )
}

export default MapView
